import { computed, type ComputedRef, type Ref, unref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { BaseError } from '../models/BaseError'
import { useMapErrorMessage } from './useMapErrorMessage'

/**
 * Mapeamento de campos para extração de erros
 *
 * Exemplos:
 * {
 *   name: 'name',                    // Erro direto: meta.name
 *   email: 'contacts.value',         // Erro aninhado: meta.contacts[].value (todos os índices)
 *   mainEmail: 'contacts.0.value',   // Erro aninhado com índice: meta.contacts[0].value (índice específico)
 *   pin: 'support.access_pin'        // Erro aninhado em objeto: meta.support.access_pin
 * }
 *
 * Exemplo com array simples (erros diretos por índice):
 * {
 *   barcodes: []
 * }
 * // Array vazio [] significa que cada índice contém diretamente os erros
 * // Retorna: barcodes[0] = ['erro'], barcodes[1] = [], barcodes[2] = ['erro']
 *
 * Exemplo com array de items (sintaxe explícita com array de objetos):
 * {
 *   items: [{
 *     amount: 'amount',
 *     stockId: 'stockId',
 *     unitaryValue: 'unitaryValue'
 *   }]
 * }
 * // A chave 'items' vira o arrayPath automaticamente
 * // Retorna: items[0] = { amount: ['erro'], stockId: [], unitaryValue: ['erro'] }
 */
export type ErrorFieldMappings = Record<string, string | [] | [Record<string, string>]>

/**
 * Extrai erros de estruturas aninhadas no meta dos BaseError
 *
 * @param errors - Array reativo de BaseError
 * @param fieldMappings - Mapeamento de campos (pode ser reativo)
 * @returns Objeto reativo com mensagens de erro traduzidas para cada campo
 */
export function useErrorMessages (
  errors: Ref<BaseError[]>,
  fieldMappings: Ref<ErrorFieldMappings> | ErrorFieldMappings
): { errorMessages: ComputedRef<Record<string, any>>, hasError: ComputedRef<boolean> } {
  const { t } = useI18n()

  /**
   * Extrai erros para um campo específico
   */
  /**
   * Navega pela estrutura hierárquica usando o path
   * Suporta índices de array numéricos (ex: contacts.0.value)
   * Também suporta chaves literais com dot notation (ex: 'suppliers.2.code')
   */
  function getNestedValue (obj: any, path: string): any {
    // Primeiro tenta acessar como chave literal (para casos como 'suppliers.2.code')
    if (obj[path] !== undefined) {
      return obj[path]
    }

    // Se não encontrar como chave literal, tenta encontrar chaves que começam com o path
    // (para casos como 'items.1.xml_options.0.barcode' onde 'items.1.xml_options' é uma chave)
    if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
      const keys = Object.keys(obj)
      // Encontra a chave mais longa que corresponde ao início do path
      const matchingKey = keys
        .filter(key => path.startsWith(key + '.'))
        .sort((a, b) => b.length - a.length)[0] // Pega a chave mais longa

      if (matchingKey) {
        const remainingPath = path.slice(matchingKey.length + 1) // Remove a chave e o ponto
        const nestedObj = obj[matchingKey]
        if (nestedObj != null) {
          return getNestedValue(nestedObj, remainingPath)
        }
      }
    }

    // Navegação hierárquica padrão
    return path.split('.').reduce((current, key) => {
      if (current == null) {
        return undefined
      }

      // Verifica se é um índice numérico
      const numericKey = Number(key)
      if (!isNaN(numericKey) && Array.isArray(current)) {
        return current[numericKey]
      }

      return current[key]
    }, obj)
  }

  /**
   * Verifica se uma parte do path é um índice numérico
   */
  function isNumericIndex (part: string): boolean {
    return /^\d+$/.test(part)
  }

  function extractFieldErrors (fieldPath: string): string[] {
    const pathParts = fieldPath.split('.')

    return errors.value
      .filter(error => error.meta != null)
      .flatMap((error) => {
        const baseData = error.meta!

        // Se o path contém um índice numérico, usar navegação hierárquica
        if (pathParts.some(part => isNumericIndex(part))) {
          const fieldError = getNestedValue(baseData, fieldPath)
          if (fieldError != null) {
            return useMapErrorMessage({ code: error.code, meta: fieldError } as BaseError)[0] || ''
          }
          return ''
        }

        if (pathParts.length === 1) {
          // Verifica se baseData é um array (caso de addresses)
          if (Array.isArray(baseData)) {
            return baseData
              .filter((item: any) => item != null && item[pathParts[0]] != null)
              .map((item: any) => {
                const errorCode = item[pathParts[0]]
                return useMapErrorMessage({ code: error.code, meta: errorCode } as BaseError)[0] || ''
              })
          } else {
            // Erro direto (ex: name)
            const fieldError = baseData[pathParts[0]]
            if (fieldError != null) {
              return useMapErrorMessage({ code: error.code, meta: fieldError } as BaseError)[0] || ''
            }
          }
          return ''
        } else if (pathParts.length === 2) {
          const [firstField, secondField] = pathParts
          const firstLevelData = baseData[firstField]

          if (Array.isArray(firstLevelData)) {
            // Erro aninhado em array (ex: contacts.value)
            return firstLevelData
              .filter((item: any) => item != null && item[secondField] != null)
              .map((item: any) => {
                const errorCode = item[secondField]
                return useMapErrorMessage({ code: error.code, meta: errorCode } as BaseError)[0] || ''
              })
          } else {
            // Erro aninhado em objeto (ex: support.access_pin)
            const fieldError = getNestedValue(baseData, fieldPath)
            if (fieldError != null) {
              return useMapErrorMessage({ code: error.code, meta: fieldError } as BaseError)[0] || ''
            }
          }
          return ''
        } else {
          // Para paths com 3+ níveis, usar navegação hierárquica
          const fieldError = getNestedValue(baseData, fieldPath)
          if (fieldError != null) {
            return useMapErrorMessage({ code: error.code, meta: fieldError } as BaseError)[0] || ''
          }
          return ''
        }
      })
      .filter(message => message.length > 0)
  }

  /**
   * Extrai erros de um array simples, onde cada índice contém diretamente os códigos de erro
   * Exemplo para barcodes: [undefined, undefined, [12]] -> [[], [], ['Erro traduzido']]
   */
  function extractSimpleArrayErrors (arrayFieldPath: string): string[][] {
    const result: string[][] = []

    errors.value
      .filter(error => error.meta != null)
      .forEach((error) => {
        const baseData = error.meta!
        const arrayData = getNestedValue(baseData, arrayFieldPath)

        if (Array.isArray(arrayData)) {
          arrayData.forEach((item: any, index: number) => {
            if (!result[index]) {
              result[index] = []
            }

            // Se o item existe e não é undefined, extrai as mensagens de erro
            if (item != null) {
              // useMapErrorMessage retorna um array de mensagens traduzidas
              const errorMessages = useMapErrorMessage({ code: error.code, meta: item } as BaseError)
              errorMessages.forEach((errorMessage) => {
                if (errorMessage) {
                  result[index].push(t(errorMessage))
                }
              })
            }
          })
        }
      })

    return result
  }

  /**
   * Extrai erros de um array de items, retornando um array de objetos de erro por índice
   * Exemplo: items[0] = { amount: ['error'], stockId: ['error'] }
   */
  function extractArrayErrors (arrayFieldPath: string, itemFieldMappings: Record<string, string>): Array<Record<string, string[]>> {
    const result: Array<Record<string, string[]>> = []

    errors.value
      .filter(error => error.meta != null)
      .forEach((error) => {
        const baseData = error.meta!
        const arrayData = getNestedValue(baseData, arrayFieldPath)

        if (Array.isArray(arrayData)) {
          arrayData.forEach((item: any, index: number) => {
            if (!result[index]) {
              result[index] = {}
              // Inicializa todos os campos para este índice
              Object.keys(itemFieldMappings).forEach((fieldName) => {
                result[index][fieldName] = []
              })
            }

            // Para cada field mapping, extrai o erro deste item específico
            Object.entries(itemFieldMappings).forEach(([fieldName, fieldPath]) => {
              const fieldError = getNestedValue(item, fieldPath)
              if (fieldError != null) {
                const errorMessage = useMapErrorMessage({ code: error.code, meta: fieldError } as BaseError)[0]
                if (errorMessage) {
                  result[index][fieldName].push(t(errorMessage))
                }
              }
            })
          })
        }
      })

    return result
  }

  // Criar computeds para cada campo mapeado
  const errorMessages = computed(() => {
    const mappings = unref(fieldMappings)
    const result: Record<string, string[] | string[][] | Array<Record<string, string[]>>> = {}

    for (const [fieldName, fieldPath] of Object.entries(mappings)) {
      if (Array.isArray(fieldPath)) {
        // Se é array vazio [], trata como array simples de erros
        if (fieldPath.length === 0) {
          result[fieldName] = extractSimpleArrayErrors(fieldName)
        // Se é array com objeto [{...}], trata como array de objetos com campos
        } else if (fieldPath.length > 0) {
          result[fieldName] = extractArrayErrors(fieldName, fieldPath[0])
        }
      } else {
        // Campo simples
        result[fieldName] = extractFieldErrors(fieldPath as string).map(message => t(message))
      }
    }

    return result
  })

  const hasError = computed(() => {
    return Object.values(errorMessages.value).some((messages) => {
      if (Array.isArray(messages)) {
        if (messages.length === 0) {
          return false
        }
        // Verifica se é array de strings ou array de objetos
        const firstItem = messages[0]
        if (typeof firstItem === 'string') {
          return true
        }
        // Se for array de objetos, verifica cada objeto
        return messages.some((item) => {
          if (typeof item === 'object' && item !== null) {
            return Object.values(item).some(arr => Array.isArray(arr) && arr.length > 0)
          }
          return false
        })
      }
      return false
    })
  })

  return {
    errorMessages,
    hasError
  }
}
